import React from 'react';
import { Link } from 'react-router-dom';
import '../css/landing-page.css';

function setMetaTag(name, content, attribute = 'name') {
    if (!content) return;
    let tag = document.querySelector(`meta[${attribute}="${name}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, name);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
}

const FeatureIcon = ({ type }) => {
    const icons = {
        timetable: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11H7v6h2V11zm4-6H9v12h4V5zm4-2h-2v14h2V3z" />
            </svg>
        ),
        integration: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="1" />
                <path d="M12 1v6m0 6v6" />
                <path d="M4.22 4.22l4.24 4.24m5.08 5.08l4.24 4.24M1 12h6m6 0h6M4.22 19.78l4.24-4.24m5.08-5.08l4.24-4.24" />
            </svg>
        ),
        live: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="1" />
                <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                <path d="M12 7v5l3 2" />
            </svg>
        ),
        growth: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 17" />
                <polyline points="17 6 23 6 23 12" />
            </svg>
        )
    };
    return icons[type] || null;
};

const STATUS_BADGES = {
    live: { label: 'Live now', className: 'rm-live-badge rm-live-badge-live' },
    upcoming: { label: 'Upcoming', className: 'rm-live-badge rm-live-badge-upcoming' },
    recent: { label: 'Recently finished', className: 'rm-live-badge rm-live-badge-recent' }
};

function formatRegattaDate(isoDate) {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function buildMeetingPath(meeting, page) {
    if (meeting.urlSlug) return `/${meeting.urlSlug}/${page}`;
    return `/${page}?meetingId=${meeting.meetingId}`;
}

function LiveRegattasSection() {
    const [meetings, setMeetings] = React.useState([]);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await fetch('/api/public/live-meetings');
                if (!response.ok) return;
                const body = await response.json();
                if (!cancelled) {
                    setMeetings(Array.isArray(body?.meetings) ? body.meetings : []);
                }
            } catch (_error) {
                // Section simply stays hidden if the lookup fails.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!meetings.length) return null;

    return (
        <section className="rm-live-regattas">
            <h2 className="rm-section-title">Live &amp; Upcoming Regattas</h2>
            <p className="rm-section-subtitle">Follow racing as it happens — no login required</p>
            <div className="rm-live-grid">
                {meetings.map(meeting => {
                    const badge = STATUS_BADGES[meeting.status] || STATUS_BADGES.upcoming;
                    const title = `${meeting.regattaName}${meeting.year ? ` ${meeting.year}` : ''}`.trim();
                    const dateLabel = formatRegattaDate(meeting.regattaDate);
                    return (
                        <article className="rm-live-card" key={meeting.meetingId}>
                            <span className={badge.className}>
                                {meeting.status === 'live' && <span className="rm-live-dot" aria-hidden="true"></span>}
                                {badge.label}
                            </span>
                            <h3 className="rm-live-card-title">{title || 'Regatta'}</h3>
                            {dateLabel && <p className="rm-live-card-date">{dateLabel}</p>}
                            <div className="rm-live-card-links">
                                <Link className="rm-btn rm-btn-primary rm-btn-sm" to={buildMeetingPath(meeting, 'live-timetable')}>
                                    Live Timetable
                                </Link>
                                <Link className="rm-btn rm-btn-outline rm-btn-sm" to={buildMeetingPath(meeting, 'draw')}>
                                    Draw
                                </Link>
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

export function LandingPage() {
    React.useEffect(() => {
        document.title = 'RegattaManager | Multi-tenant Regatta Software with BROE Integration';
        setMetaTag('description', 'RegattaManager helps rowing clubs run regattas with live timetable, draw, results, and British Rowing Online (BROE) integration. Launch your tenant at regattamanager.co.uk.');
        setMetaTag('keywords', 'rowing regatta software, BROE integration, British Rowing Online, regatta management, live timetable, draw management');
        setMetaTag('og:title', 'RegattaManager for Rowing Regattas', 'property');
        setMetaTag('og:description', 'Create your own regatta tenant, publish live timetable and draw, and manage officials and entries with BROE integration.', 'property');
    }, []);

    return (
        <main className="rm-landing">
            {/* Hero Section */}
            <section className="rm-hero">
                <div className="rm-hero-content">
                    <div className="rm-hero-text">
                        <h1 className="rm-hero-title">
                            Regatta Management Made Simple
                        </h1>
                        <p className="rm-hero-subtitle">
                            Professional-grade regatta management software built for UK rowing. Manage your event with live timetables, 
                            integrated BROE connection, and seamless collaboration—all from a single, intuitive control room.
                        </p>
                        <div className="rm-hero-cta">
                            <Link className="rm-btn rm-btn-primary" to="/signup">
                                Start Your Regatta
                                <span className="rm-btn-arrow">→</span>
                            </Link>
                            <Link className="rm-btn rm-btn-secondary" to="/login">Learn More</Link>
                        </div>
                        <p className="rm-hero-footnote">
                            <strong>Example:</strong> https://sar.regattamanager.co.uk/2026
                        </p>
                    </div>
                    <div className="rm-hero-visual">
                        <div className="rm-hero-graphic">
                            <div className="rm-graphic-element rm-graphic-1"></div>
                            <div className="rm-graphic-element rm-graphic-2"></div>
                            <div className="rm-graphic-element rm-graphic-3"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Live & Upcoming Regattas */}
            <LiveRegattasSection />

            {/* Features Section */}
            <section className="rm-features">
                <h2 className="rm-section-title">Everything You Need</h2>
                <p className="rm-section-subtitle">Comprehensive tools for modern regatta management</p>
                
                <div className="rm-features-grid">
                    <article className="rm-feature-card">
                        <div className="rm-feature-icon">
                            <FeatureIcon type="timetable" />
                        </div>
                        <h3>Live Timetable & Draw</h3>
                        <p>
                            Publish timetables and draws in real-time. Your competitors and supporters stay informed without needing app downloads.
                        </p>
                    </article>

                    <article className="rm-feature-card">
                        <div className="rm-feature-icon">
                            <FeatureIcon type="integration" />
                        </div>
                        <h3>BROE Integration</h3>
                        <p>
                            Connect seamlessly with British Rowing Online. Manage credentials at tenant or meeting level with flexible overrides.
                        </p>
                    </article>

                    <article className="rm-feature-card">
                        <div className="rm-feature-icon">
                            <FeatureIcon type="live" />
                        </div>
                        <h3>Real-Time Updates</h3>
                        <p>
                            Race status, results, and commentary update instantly. Officials and organizers stay perfectly synchronized.
                        </p>
                    </article>

                    <article className="rm-feature-card">
                        <div className="rm-feature-icon">
                            <FeatureIcon type="growth" />
                        </div>
                        <h3>Multi-Event Support</h3>
                        <p>
                            Start with one regatta or manage unlimited events. Each tenant gets its own domain with year-based URLs.
                        </p>
                    </article>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="rm-how-it-works">
                <h2 className="rm-section-title">How It Works</h2>
                
                <div className="rm-steps">
                    <div className="rm-step">
                        <div className="rm-step-number">1</div>
                        <h4>Create Tenant</h4>
                        <p>Sign up and get your organization's dedicated tenant domain</p>
                    </div>
                    <div className="rm-step-arrow">→</div>
                    <div className="rm-step">
                        <div className="rm-step-number">2</div>
                        <h4>Set Up Regatta</h4>
                        <p>Configure your event, connect BROE, and add participants</p>
                    </div>
                    <div className="rm-step-arrow">→</div>
                    <div className="rm-step">
                        <div className="rm-step-number">3</div>
                        <h4>Manage Live</h4>
                        <p>Run your event with live updates visible to all stakeholders</p>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="rm-pricing">
                <h2 className="rm-section-title">Simple, Transparent Pricing</h2>
                <p className="rm-section-subtitle">Choose the plan that fits your needs</p>
                
                <div className="rm-pricing-cards">
                    <div className="rm-price-card">
                        <div className="rm-price-header">
                            <h3>One-Off Regatta</h3>
                            <p className="rm-price-label">For occasional organizers</p>
                        </div>
                        <div className="rm-price-amount">
                            <span className="rm-currency">£</span>
                            <span className="rm-value">49.95</span>
                        </div>
                        <p className="rm-price-description">
                            Includes one regatta creation and year-long tenant access
                        </p>
                        <Link className="rm-btn rm-btn-outline" to="/signup">Get Started</Link>
                        <ul className="rm-price-features">
                            <li>✓ One regatta</li>
                            <li>✓ Custom tenant domain</li>
                            <li>✓ BROE integration</li>
                            <li>✓ Live updates</li>
                        </ul>
                    </div>

                    <div className="rm-price-card rm-price-card-featured">
                        <div className="rm-price-badge">POPULAR</div>
                        <div className="rm-price-header">
                            <h3>Annual Unlimited</h3>
                            <p className="rm-price-label">For active programs</p>
                        </div>
                        <div className="rm-price-amount">
                            <span className="rm-currency">£</span>
                            <span className="rm-value">79.95</span>
                        </div>
                        <p className="rm-price-description">
                            Unlimited regattas for 12 months
                        </p>
                        <Link className="rm-btn rm-btn-primary" to="/signup">Get Started</Link>
                        <ul className="rm-price-features">
                            <li>✓ Unlimited regattas</li>
                            <li>✓ Custom tenant domain</li>
                            <li>✓ BROE integration</li>
                            <li>✓ Live updates</li>
                            <li>✓ Email support</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* CTA Footer Section */}
            <section className="rm-footer-cta">
                <div className="rm-footer-content">
                    <h2>Ready to Elevate Your Regatta?</h2>
                    <p>Join UK rowing organizations that trust RegattaManager for their events</p>
                    <Link className="rm-btn rm-btn-primary rm-btn-large" to="/signup">
                        Create Your Tenant Today
                        <span className="rm-btn-arrow">→</span>
                    </Link>
                    <p className="rm-hero-footnote">
                        <Link to="/privacy-policy">Read our Privacy Policy</Link>
                    </p>
                </div>
            </section>
        </main>
    );
}
